// Acceso al backend de suscripciones (§3 de
// ../creciente-fertil-app/docs/suscripciones-flujo-propuesto.md).
//
// Ojo con el modelo: la app LEE el estado de suscripción, nunca lo escribe. Lo
// único que se manda desde acá es el alta del checkout (que devuelve un link de
// Mercado Pago, no un plan asignado) y la cancelación, que es gestión de cuenta.
// El nivel del usuario NO se toca desde el front: lo escribe el backend cuando
// MP confirma el pago. El `guardarNivel()` que hacía eso era el agujero por el
// que cualquiera se asignaba un plan pago gratis.
import { getUser } from "$lib/userstorage/usersotrage";
import { handleAuthenticatedRequest, jsonOrThrow } from "../errores/erroresback";

let ruta_java = import.meta.env.VITE_RUTA_JAVA_SERVER;
let ruta_local_java = import.meta.env.VITE_RUTA_LOCAL_JAVA_SERVER;
let bd_local = import.meta.env.VITE_LOCAL_BD == "si";
let RUTA_JAVA = bd_local ? ruta_local_java : ruta_java;
const RUTA_SUBS = "subscriptions";

function authHeaders() {
    let user = getUser();
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`,
    };
}

function procesarPlan(p) {
    return {
        id: p.planId,
        nombre: p.name,
        nivel: p.level,
        // `precio` es el monto en ARS que cobra el preapproval de MP;
        // `precioUsd` es el de exhibición (decisión de negocio #2: se muestra en
        // dólares y se cobra en pesos). Los dos se actualizan a mano.
        precio: p.price,
        precioUsd: p.priceUsd,
        maxEstablecimientos: p.maxEstablishments,
        maxUsuarios: p.maxUsers,
        maxAnimales: p.maxAnimals,
    };
}

function procesarSuscripcion(s) {
    if (!s) return null;
    return {
        id: s.subscriptionId,
        planId: s.planId,
        plan: s.planName,
        nivel: s.level,
        // ACTIVE | PAST_DUE | CANCELLED | PENDING | REPLACED
        estado: s.status,
        // Hasta cuándo llega lo pagado: es el "se renueva el ..." y, si está
        // cancelada, el "finaliza el ...".
        finPeriodo: s.periodEnd,
        inicio: s.startDate,
        // Cuándo se dio de baja, no hasta cuándo hay acceso (eso es finPeriodo).
        baja: s.endDate,
    };
}

// `max: null` significa SIN TOPE (plan Ilimitado), no cero: quien dibuje la
// barra tiene que tratarlo como "sin barra", no como 0.
function procesarTramo(t) {
    if (!t) return null;
    return { usado: t.current, tope: t.max };
}

function procesarUso(u) {
    return {
        plan: u.planName,
        nivel: u.planLevel,
        animales: procesarTramo(u.animals),
        establecimientos: procesarTramo(u.establishments),
        // null cuando el token no tiene establecimiento en scope: `maxUsers` es
        // un tope por establecimiento, sin uno no hay nada que mostrar.
        usuarios: procesarTramo(u.users),
    };
}

// El catálogo de planes. Es la ÚNICA fuente de precios (decisión de negocio #3):
// los stores `niveles.js` y `planes.js` se eliminan, no se sincronizan.
//
// El endpoint es público porque lo consume `/planes` sin login, así que va con
// fetch directo en vez de `handleAuthenticatedRequest`: no hay sesión que
// refrescar ni 401 que interpretar.
export async function getPlanes() {
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/plans`;
    let res = await fetch(ruta, { headers: { "Content-Type": "application/json" } });
    let data = await jsonOrThrow(res, "suscripciones.getPlanes");
    return data.map(procesarPlan);
}

// La suscripción vigente del usuario logueado. Puede volver null: alguien que
// nunca tuvo ninguna.
//
// El userId del path lo ignora el backend (lo saca del token); se manda porque
// la ruta lo pide.
export async function getMiSuscripcion() {
    let user = getUser();
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/my/${user.id}`;
    let res = await handleAuthenticatedRequest(ruta, { headers: authHeaders() });
    let data = await jsonOrThrow(res, "suscripciones.getMiSuscripcion");
    return procesarSuscripcion(data);
}

// Cuánto tiene usado del plan, para las barras de "Mi plan".
export async function getUso() {
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/usage`;
    let res = await handleAuthenticatedRequest(ruta, { headers: authHeaders() });
    let data = await jsonOrThrow(res, "suscripciones.getUso");
    return procesarUso(data);
}

// Arranca el checkout: devuelve el `initPoint` de Mercado Pago al que hay que
// mandar al usuario. NO asigna ningún plan — eso pasa cuando MP confirma el pago
// y llama al webhook. Se usa solo desde la página pública `/planes`.
export async function crearSuscripcion(planId) {
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}`;
    let res = await handleAuthenticatedRequest(ruta, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ planId }),
    });
    let data = await jsonOrThrow(res, "suscripciones.crearSuscripcion");
    return { initPoint: data.initPoint, suscripcionId: data.subscriptionId };
}

// Lo mismo, pero para quien llegó por el link de un mail y no tiene sesión en
// este navegador. La llave del link (`?t=`) hace de autorización.
//
// Hace falta porque el mail es el único camino hacia /planes —la app no linkea
// la compra en ninguna plataforma— y se abre en el navegador, donde la sesión de
// la app no llega. Sin esto había que loguearse en el medio de la compra, y ahí
// el recorrido se cortaba: si el login perdía el rastro de a dónde volver,
// terminabas en el inicio y no había forma de volver a la página de planes.
//
// Va con fetch directo y no con `handleAuthenticatedRequest`: no hay sesión que
// refrescar ni 401 que interpretar. La llave no es una sesión y el backend no
// devuelve ninguna: la respuesta es el link de pago y nada más.
export async function crearSuscripcionPorLink(token, planId) {
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/checkout-por-link`;
    let res = await fetch(ruta, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, planId: String(planId) }),
    });
    let data = await jsonOrThrow(res, "suscripciones.crearSuscripcionPorLink");
    return { initPoint: data.initPoint };
}

// La vuelta del checkout: le pasa al backend el `preapproval_id` con el que
// Mercado Pago volvió, para que ate ese pago a esta cuenta y active el plan si
// ya está cobrado. Devuelve la suscripción como quedó.
//
// Hace falta porque el checkout de Mercado Pago es el del PLAN, y eso es lo que
// permite pagar con una cuenta de MP de otro mail (antes se mandaba el mail de
// la app como pagador y MP rebotaba con "Tu e-mail no coincide con el de la
// suscripción"). A cambio, el pago vuelve sin nada que diga de quién es: este id
// es el único vínculo.
//
// NO viola la regla de oro: acá no se escribe ningún nivel ni estado desde el
// navegador — se le pasa un id al backend, que le pregunta a Mercado Pago.
export async function vincularPago(preapprovalId) {
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/vincular`;
    let res = await handleAuthenticatedRequest(ruta, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ preapprovalId }),
    });
    let data = await jsonOrThrow(res, "suscripciones.vincularPago");
    return procesarSuscripcion(data);
}

// Cancela la suscripción. El acceso NO se corta acá: dura hasta el fin del
// período ya pagado (`finPeriodo`), y recién ahí cae al plan Gratuito.
export async function cancelarSuscripcion() {
    let user = getUser();
    let ruta = `${RUTA_JAVA}${RUTA_SUBS}/cancel/${user.id}`;
    let res = await handleAuthenticatedRequest(ruta, {
        method: "POST",
        headers: authHeaders(),
    });
    // No devuelve body: alcanza con que no haya fallado.
    if (!res.ok) {
        const err = new Error(`suscripciones.cancelarSuscripcion -> ${res.status}`);
        err.status = res.status;
        throw err;
    }
}
